<template>
  <span>
    <event-detail-modal
      v-model="detailOpen"
      :filters="payload.filter"
      :pagination-enabled="true"
    />
    <v-container>
      <date-picker
        v-if="history"
        :text="datePickerText"
        raised
        history
        style="margin: 0 0 20px 0;"
        @update-date="handleDateUpdate"
      />
      <div class="mt-5">
        <valkyrie-list
          :list-data="listData"
          :pagination="eventsListPagination"
          :pagination-text="paginationText"
          :list-name="'Events'"
          :headers.sync="headers"
          :is-loading="loading"
          sortable
          rows-selectable
          @update-pagination="handleUpdatePagination"
          @select-row="selectRow"
          @update-sort="handleUpdateSort"
        >
          <template v-slot:list-header>
            <default-list-header
              :list-name="'Events'"
              :searchable="false"
              :exportable="exportable && hasDataToQuery"
              :filterable="filterable && hasDataToQuery"
              :pagination-text="paginationText"
              @search="handleUpdateSearch"
              @export-csv="exportCSV(exportName)"
              @clear-filters="handleClearFilters"
            >
              <template #event-type>
                <multiselect-filter
                  :items="eventTypes"
                  filter-key="event_class"
                  @apply-multiselect="handleUpdateFilters"
                />
              </template>
              <template #start-time>
                <date-filter
                  filter-key="start_time"
                  @apply-date-filter="handleUpdateFilters"
                />
              </template>
              <template #end-time>
                <date-filter
                  filter-key="end_time"
                  @apply-date-filter="handleUpdateFilters"
                />
              </template>
              <template #duration>
                <duration-filter
                  filter-key="duration"
                  @apply-duration-filter="handleUpdateFilters"
                />
              </template>
            </default-list-header>
          </template>
          <!-- eslint-disable vue/valid-v-slot -->
          <template #item.eventClass="{ item }">
            <v-icon
              color="#535353"
              :size="setIconSize(item.eventClass)"
              style="margin-right:10px"
            >
              {{ getEventIcon(item.eventClass) }}
            </v-icon>
            <span
              style="font-size: 16px;"
              class="font-weight-bold ml-2"
            >{{ formatEvent(item.eventClass) }}</span>
          </template>

          <template #item.start_time="{ item }">
            {{ formatEventDate(item.utcStartDateTime, item.property.timezone) }}
            <span class="font-weight-bold">{{ formatEventTime(item.utcStartDateTime, item.property.timezone) }}</span>
            <span
              v-if="isValidDate(item.utcStartDateTime)"
              class="grey--text lighten-4"
            >
              {{ `(${formatTimezone(item.utcStartDateTime, item.property.timezone)})` }}
            </span>
          </template>
          <template #item.end_time="{ item }">
            <template v-if="isValidDate(item.utcEndDateTime)">
              {{ formatEventDate(item.utcEndDateTime, item.property.timezone) }}
              <span class="font-weight-bold">{{ formatEventTime(item.utcEndDateTime, item.property.timezone) }}</span>
              <span
                v-if="isValidDate(item.utcEndDateTime)"
                class="grey--text lighten-4"
              >
                {{ `(${formatTimezone(item.utcEndDateTime, item.property.timezone)})` }}
              </span>
            </template>
            <template v-else>Open</template>
          </template>

          <template #item.durationReadable="{ item }">
            {{ isValidDate(item.utcEndDateTime) ? inHoursMinutes(item.duration) : '-' }}
          </template>
          <template slot="empty-state">
            <empty-state
              icon="$faCalendarLight"
              text="All's quiet at this property"
            />
          </template>
        </valkyrie-list>
      </div></v-container>
  </span>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { DateTime } from 'luxon'
  import DatePicker from '@/components/base/DatePicker.vue'
  import DatePickerMixin from '@/helpers/date-picker-mixin'
  import listMixin from '@/helpers/list-mixin'
  import listControllerMixin from '@/helpers/list-controller-mixin'
  import ValkyrieList from '@/components/ValkyrieList.vue'
  import DefaultListHeader from '@/components/ValkyrieListDefaultHeader.vue'
  import EmptyState from '../../components/base/EmptyState.vue'
  import MultiselectFilter from '@/components/filters/MultiselectFilter.vue'
  import EventDetailModal from '../../components/events/EventDetailModal.vue'
  import DateFilter from '@/components/filters/DateFilter.vue'
  import DurationFilter from '@/components/filters/DurationFilter.vue'
  import paginationHelper from '@/helpers/pagination-helper'
  import { eventTypes, eventLabels } from '@/helpers/event-helpers'
  import { OCCUPANCY } from '../../helpers/feature-constants'

  export default {
    name: 'EventsList',
    components: {
      ValkyrieList,
      DefaultListHeader,
      EmptyState,
      MultiselectFilter,
      DurationFilter,
      DateFilter,
      DatePicker,
      EventDetailModal,
    },
    mixins: [listMixin, listControllerMixin, DatePickerMixin],
    props: {
      searchable: {
        type: Boolean,
        default: true,
      },
      exportable: {
        type: Boolean,
        default: true,
      },
      filterable: {
        type: Boolean,
        default: true,
      },
      hasFilters: {
        type: Boolean,
        default: false,
      },
      filterBy: {
        type: String,
        default: '',
      },
      history: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      payload: {
        listType: 'events',
      },
      hasDataToQuery: false,
      dateRangeFilter: [],
      detailOpen: false,
      orgOccupancyEligible: false,
    }),
    computed: {
      ...mapGetters({
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        totalEvents: 'Events/totalEvents',
        getOrgFeatureEligibility: 'Organizations/getOrgFeatureEligibility',
      }),
      headers () {
        return [
          {
            text: 'Type',
            value: 'eventClass',
            sortable: false,
            width: 220,
          },
          ...(this.$route.name !== 'Property Details' ? [{
            text: 'Property',
            value: 'property.name',
            sortable: false,
          }] : []),
          {
            text: 'Sensor',
            value: 'activityZone.activityZoneName',
            sortable: false,
          },
          {
            text: 'Start Time',
            value: 'start_time',
          },
          {
            text: 'End Time',
            value: 'end_time',
          },
          {
            text: 'Duration',
            value: 'durationReadable',
            sortable: false,
          },
        ]
      },
      paginationText () {
        return paginationHelper(this.eventsListPagination.pageNum, this.eventsListPagination.perPage, this.eventsListPagination.total)
      },
      eventTypes () {
        const types = [
          { name: eventLabels.NOISE, value: eventTypes.NOISE },
          { name: eventLabels.DISCONNECT, value: eventTypes.DISCONNECT },
          { name: eventLabels.TAMPER, value: eventTypes.TAMPER },
          { name: eventLabels.BATTERY, value: eventTypes.BATTERY },
          { name: eventLabels.LOST_NETWORK, value: eventTypes.LOST_NETWORK },
        ]
        if (this.orgOccupancyEligible) {
          types.push({ name: eventLabels.ELEVATED_OCCUPANCY, value: eventTypes.ELEVATED_OCCUPANCY })
        }
        return types
      },
      exportName () {
        if (this.$route.name === 'Property Details') {
          return this.$store.getters.getNameForDetailPage('properties') + ' Events'
        }
        return 'Events'
      },
      defaultSort () {
        return [{ key: 'start_time', order: 'DESC' }]
      },
      staticFilters () {
        return [
          ...this.dateRangeFilter,
          ...(this.filterBy ? [{ key: this.filterBy, operator: '=', value: this.$route.params.id }] : []),
          ...(this.currentSelectedOrg.id && this.$route.name !== 'Property Details' ? [{ key: 'organization_id', operator: '=', value: `${this.currentSelectedOrg.id}` }] : []),
          { key: 'event_type', operator: '=', value: 'EXTERNAL' },
        ]
      },
      listAPIPath () {
        return `${this.$store.state.baseURL}/events/list`
      },
      eventsListPagination () {
        return { ...this.pagination, total: this.totalEvents }
      },
    },
    watch: {
      currentSelectedOrg (newOrg) {
        this.initializeEventsList()
        if (newOrg.id) {
          this.$store.dispatch('Organizations/fetchOrgFeatureEligibility', { orgId: newOrg.id, feature: OCCUPANCY })
            .then(this.setOccupancyEligibility)
        }
      },
    },
    created () {
      this.initializeEventsList()
    },
    destroyed () {
      clearInterval(this.timeout)
    },
    methods: {
      setOccupancyEligibility () {
        this.orgOccupancyEligible = this.getOrgFeatureEligibility(OCCUPANCY)
      },
      formatTimezone (dateTime, timezone) {
        return DateTime.fromISO(dateTime).setZone(timezone).toFormat('ZZZZ')
      },
      formatEventDate (dateTime, timezone) {
        return DateTime.fromISO(dateTime).setZone(timezone).toFormat('dd LLL yyyy')
      },
      formatEventTime (dateTime, timezone) {
        return DateTime.fromISO(dateTime).setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE)
      },
      setIconSize (val) {
        switch (val) {
          case 'NOISE':
            return 20
          case 'LOST_NETWORK':
            return 18
          default:
            return 17
        }
      },
      handleDateUpdate ({ dates }) {
        this.dates = dates
        this.dateRangeFilter = [
          {
            key: 'start_time',
            operator: '>=',
            value: this.dates.startDate.toISOString(),
          },
          {
            key: 'end_time',
            operator: '<=',
            value: this.dates.endDate.toISOString(),
          },
        ]
        this.fetchList()
      },
      initializeEventsList () {
        this.fetchList().then(res => {
          this.updateOccupancySensorLabels(res.data.data)
          this.updateTotal(res.data.pagination.total)
          this.hasDataToQuery = res.data.pagination.total > 0
        })
      },
      updateTotal (total) {
        this.$store.commit('Events/updateTotalEvents', total)
      },
      updateOccupancySensorLabels (data) {
        if (!data) {
          return
        }

        data.forEach(event => {
          if (event.eventClass === eventTypes.ELEVATED_OCCUPANCY) {
            event.activityZone = {
              activityZoneName: 'N/A',
            }
          }
        })
      },
      selectRow (item) {
        this.$store.commit('Events/updateSelectedEvent', item)
        const { perPage, pageNum } = this.pagination
        const eventIndex = this.listData.findIndex(evt => evt.id === item.id)
        const selectedEventIndex = perPage * (pageNum - 1) + (eventIndex + 1)
        this.$store.commit('Events/updateSelectedEventIndex', selectedEventIndex)
        this.detailOpen = true
      },
    },
  }
</script>
